import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import type { AdminCoupon } from '@/api/admin';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDate } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/cupones/')({
  component: AdminCouponsPage,
});

function AdminCouponsPage(): JSX.Element {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [percent, setPercent] = useState('100');
  const [desc, setDesc] = useState('');

  const couponsQ = useQuery({ queryKey: ['admin-coupons'], queryFn: adminApi.listCoupons });

  const invalidate = (): void => void qc.invalidateQueries({ queryKey: ['admin-coupons'] });
  const onError = (e: unknown): void => setError(toApiError(e).message);

  const createMut = useMutation({
    mutationFn: () => adminApi.createCoupon({ code, percentOff: Number(percent), description: desc }),
    onSuccess: () => {
      setCode('');
      setDesc('');
      setPercent('100');
      invalidate();
    },
    onError,
  });
  const deactivateMut = useMutation({
    mutationFn: (c: string) => adminApi.deactivateCoupon(c),
    onSuccess: invalidate,
    onError,
  });

  if (couponsQ.isLoading) return <LoadingScreen message="Cargando cupones…" />;

  const coupons: AdminCoupon[] = couponsQ.data ?? [];
  const validPercent = Number(percent) === 100;
  const canCreate = code.trim().length > 0 && validPercent && !createMut.isPending;

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOffer color="primary" /> Cupones
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Catálogo persistente de beneficios asignables a una cuenta desde su detalle. Hoy una
          bonificación del 100% evita crear el checkout y cobrar; los demás porcentajes quedan
          registrados, pero todavía no modifican automáticamente el importe del checkout.
        </Typography>
      </Stack>

      {error ? (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Nuevo cupón
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <TextField
              size="small"
              label="Código"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label="% bonificación"
              type="number"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              error={percent !== '' && !validPercent}
              helperText="Sólo 100% tiene efecto automático"
              sx={{ width: 200 }}
            />
            <TextField
              size="small"
              label="Descripción"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Button variant="contained" disabled={!canCreate} onClick={() => createMut.mutate()}>
              {createMut.isPending ? <CircularProgress size={18} /> : 'Crear'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {coupons.length === 0 ? (
        <Alert severity="info">Todavía no hay cupones.</Alert>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>% off</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Creado</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.map((c) => (
                  <TableRow key={c.code} hover>
                    <TableCell sx={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{c.code}</TableCell>
                    <TableCell>{c.percentOff}%</TableCell>
                    <TableCell>{c.description || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={c.active ? 'success' : 'default'}
                        variant={c.active ? 'filled' : 'outlined'}
                        label={c.active ? 'Activo' : 'Inactivo'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(c.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {c.active ? (
                        <Button
                          size="small"
                          color="warning"
                          disabled={deactivateMut.isPending}
                          onClick={() => deactivateMut.mutate(c.code)}
                        >
                          Desactivar
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
